import { createSelector } from 'reselect';
import { PapiGallection } from '@wix/da-papi-types';
import {
  getDenormalizedDataEntityById,
  getDenormalizedDataEntitiesByIds,
} from '@wix/da-shared-react/pkg/redux/entities';
import { selectors as streamSelectors } from '@wix/da-shared-react/pkg/Stream';
import { GallectionType } from '@wix/da-shared-react/pkg/types/gallection';
import { ALL_FOLDER_ID } from '@wix/da-url';
import { AppState } from '../../types/store';
import { getGallectionSectionType, getSelectedFolderId } from './gallectionSection';
import {
  getStreamIdForGallectionType,
  getSchemaForGallectionType,
  filterOutEmpty,
} from '../utils/gallection';
import { getFolderDeviationsStreamId } from '../streams/folderDeviations';

export const getFolderById = (
  state: AppState,
  gallectionType: GallectionType,
  id: number
): PapiGallection | undefined =>
  getDenormalizedDataEntityById(state, getSchemaForGallectionType(gallectionType), id);

export const getFoldersByIds = (state: AppState, gallectionType: GallectionType, ids: number[]) => {
  return getDenormalizedDataEntitiesByIds(state, getSchemaForGallectionType(gallectionType), ids);
};

export function getFoldersForCurrentSection(state: AppState) {
  const folderType = getGallectionSectionType(state);
  return getFoldersByGallectionType(state, folderType);
}

export const getNonEmptyFolders = createSelector([getFoldersForCurrentSection], folders =>
  filterOutEmpty(folders)
);

function getFolderIdsByType(state: AppState, gallectionType: GallectionType) {
  return streamSelectors.getItems<number>(state, getStreamIdForGallectionType(gallectionType));
}

function getFoldersByGallectionType(
  state: AppState,
  gallectionType: GallectionType
): PapiGallection[] {
  return getFoldersByIds(state, gallectionType, getFolderIdsByType(state, gallectionType));
}

export const getFeaturedFolder = createSelector(
  [getFoldersForCurrentSection],
  folders => folders.find(({ name }) => name === 'Featured') || folders[0]
);

export const getScrapsFolder = createSelector([getFoldersForCurrentSection], folders =>
  folders.find(({ name }) => name === 'Scraps')
);

export const getAllDeviationsFolder = createSelector([getNonEmptyFolders], folders =>
  folders.find(({ folderId }) => folderId === ALL_FOLDER_ID)
);

const checkFoldersHaveDeviations = (folders: PapiGallection[]): boolean => {
  const allFolder = folders.find(f => f.folderId === ALL_FOLDER_ID);
  if (allFolder && allFolder.size === 0) {
    return false;
  }

  return folders.length > 0;
};

export const hasAnyDeviations = (state: AppState): boolean => {
  const gallectionType = getGallectionSectionType(state);
  const selectedFolderId = getSelectedFolderId(state);

  // If selectedFolderId is not set yet, check if we have any folders at all
  // This handles the race condition where isSectionLoading becomes false
  // before selectedFolderId is set in state
  if (selectedFolderId === undefined || selectedFolderId === null) {
    return checkFoldersHaveDeviations(getNonEmptyFolders(state));
  }

  const streamId = getFolderDeviationsStreamId(gallectionType, selectedFolderId);
  const streamItems = streamSelectors.getItems(state, streamId) || [];

  if (streamItems.length > 0) {
    return true;
  }

  return checkFoldersHaveDeviations(getNonEmptyFolders(state));
};
